import { attach, createEffect } from 'effector';
import * as Sentry from '@sentry/browser';

import { $token } from 'shared/auth';
import { API_BASE_URL, LOCAL_API_BASE_URL, REACT_APP_ENVIRONMENT } from 'shared/config';
import { paths as Paths } from 'shared/types/APISchema';

import {
  type ApiResponse,
  type FetchArgType,
  Fetcher,
  type FetchReturnType,
  type Middleware,
  type TypedFetch,
} from 'openapi-typescript-fetch';

const fetcher = Fetcher.for<Paths>();

const sentryMiddleware: Middleware = async (url, init, next) => {
  const response = await next(url, init);

  if (!response.ok) {
    Sentry.captureException(response);
  }

  return response;
};

const responseMiddleware: Middleware = async (url, init, next): Promise<ApiResponse['data']> => {
  const response = await next(url, init);
  if (response.ok) {
    return response.data;
  }

  return response;
};

fetcher.configure({
  baseUrl: API_BASE_URL,
  use: [responseMiddleware, sentryMiddleware],
});

export const createRequest =
  <A extends TypedFetch<any>>(request: A, init?: RequestInit) =>
  async (args: FetchArgType<A>) => {
    const res = await request(args, init);

    if (!res) {
      throw new Error('answer is empty');
    }

    return res as FetchReturnType<A>;
  };
type Params<A extends TypedFetch<any>> = {
  args: FetchArgType<A>;
  init: RequestInit;
};

const credentialsParams =
  process.env.NODE_ENV === 'development' ? (LOCAL_API_BASE_URL ? { credentials: 'include' as RequestCredentials } : null) : null;

const createRequestFx = <A extends TypedFetch<any>>(req: A) => {
  const requestEffect = createEffect<Params<A>, FetchReturnType<A>, any>(({ args, init }) => createRequest(req, init)(args));

  const effectWithHeader = attach({
    effect: requestEffect,
    source: $token,
    mapParams: (args: FetchArgType<A>, token) => ({
      args,
      init: {
        ...credentialsParams,
        headers: {
          authorization: `Bearer ${token}`,
        },
      },
    }),
  });

  return effectWithHeader;
};

export const uploadFileFx = createEffect(async ({ file }: { file: File }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/files/images/`, {
      method: 'POST',
      ...credentialsParams,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/octet-stream',
        'x-file-name': `file.${file.name.split('.').at(-1)}`,
        authorization: `Bearer ${$token.getState()}`,
      },
      body: file,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Failed to upload file');
    }

    return await response.json();
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Unknown error occurred during file upload');
  }
});

export const yandexReachGoalFx = createEffect(({ id, name }: { id: number; name: string }) => {
  if (REACT_APP_ENVIRONMENT === 'prod') {
    //@ts-ignore
    ym(id, 'reachGoal', name);
  }
});

export const getCurrentEmployeeFx = createRequestFx(fetcher.path('/employees/current/').method('get').create());

export const getBlocksFx = createRequestFx(fetcher.path('/blocks/').method('get').create());

export const getTeamFx = createRequestFx(fetcher.path('/teams/{team_id}/').method('get').create());

export const getEmployeeInfoFx = createRequestFx(fetcher.path('/employees/{employee_id}/').method('get').create());

export const getEmployeeProfileInfoFx = createRequestFx(fetcher.path('/employees/{employee_id}/profile/').method('get').create());

export const getProductBySlugFx = createRequestFx(fetcher.path('/products/{slug}/').method('get').create());

export const searchEmployeeFx = createRequestFx(fetcher.path('/generic-search/').method('get').create());

export const getVacanciesFx = createRequestFx(fetcher.path('/vacancies/').method('get').create());

export const getVacanciesInTeamFx = createRequestFx(fetcher.path('/vacancies/').method('get').create());

export const getGradesFx = createRequestFx(fetcher.path('/references/grades/').method('get').create());

export const getRolesFx = createRequestFx(fetcher.path('/references/roles/').method('get').create());

export const getStacksFx = createRequestFx(fetcher.path('/references/stacks/').method('get').create());

export const getTeamsFx = createRequestFx(fetcher.path('/references/teams/').method('get').create());

export const createEmployeeFx = createRequestFx(fetcher.path('/employees/').method('post').create());

export const createVacancyFx = createRequestFx(fetcher.path('/vacancies/').method('post').create());

export const getVacancyFx = createRequestFx(fetcher.path('/vacancies/{vacancy_id}/').method('get').create());

export const getVacancyChannelsFx = createRequestFx(fetcher.path('/vacancies/search-channels/').method('get').create());

export const deleteVacancyFx = createRequestFx(fetcher.path('/vacancies/{vacancy_id}/').method('patch').create());

export const getFeedbackProcessByIdFx = createRequestFx(
  fetcher.path('/talents/soft-skill-processes/{process_id}/:get-for-evaluating').method('get').create()
);

export const sendFeedbackFx = createRequestFx(fetcher.path('/talents/soft-skill-feedbacks/').method('post').create());

export const requestSalaryFx = createRequestFx(fetcher.path('/salary-requests/').method('post').create());

export const pauseRequestSalaryFx = createRequestFx(
  fetcher.path('/salary-requests/{salary_request_id}/:pause').method('patch').create()
);
export const resumeRequestSalaryFx = createRequestFx(
  fetcher.path('/salary-requests/{salary_request_id}/:resume').method('patch').create()
);

export const getMySalaryRequestFx = createRequestFx(fetcher.path('/salary-requests/:getMyActive').method('get').create());

export const getSalaryRequestFx = createRequestFx(fetcher.path('/salary-requests/{salary_request_id}/').method('get').create());

export const startFeedbackProcessForEmployeeFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/soft-skill-processes/:start-manual').method('post').create()
);

export const getFeedbackProcessFromEmployeeFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/soft-skill-processes/').method('get').create()
);

export const getReviewProcessFromEmployeeFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/review-processes/').method('get').create()
);

export const getAvailableManualProcessesFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/manual-processes/').method('get').create()
);

export const getFeedbackForEmployeeFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/soft-skill-results/').method('get').create()
);

export const startReviewProcessFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/review-processes/').method('post').create()
);

export const sendReviewMaterialsFX = createRequestFx(
  fetcher.path('/talents/review-processes/{process_id}/reviewer-materials/').method('post').create()
);

export const getReviewProcessFx = createRequestFx(
  fetcher.path('/talents/review-processes/{process_id}/:get-for-evaluating').method('get').create()
);

export const sendReviewFX = createRequestFx(
  fetcher.path('/talents/review-requests/{review_request_id}/reviews/').method('post').create()
);

export const getReviewRequestFx = createRequestFx(
  fetcher.path('/talents/review-requests/{review_request_id}/:get-for-reviewer').method('get').create()
);

export const getUserPermissionsFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/talents-permissions/').method('get').create()
);

export const getFeedbackProcessesListFx = createRequestFx(fetcher.path('/talents/soft-skill-processes/').method('get').create());

export const getReviewProcessesListFx = createRequestFx(fetcher.path('/talents/review-processes/').method('get').create());

export const updateRequestSalaryFx = createRequestFx(
  fetcher.path('/salary-requests/{salary_request_id}/').method('patch').create()
);

export const getPastRequestsSalaryFx = createRequestFx(fetcher.path('/salary-requests/:getByEmployee').method('get').create());

export const assignReviewerToReviewRequestFx = createRequestFx(
  fetcher.path('/talents/review-requests/{review_request_id}/:reviewer-manual-assignment/').method('put').create()
);

export const getReviewProcessByIdFx = createRequestFx(
  fetcher.path('/talents/review-processes/{process_id}/').method('get').create()
);

export const getReviewerListFx = createRequestFx(fetcher.path('/talents/reviewers/').method('get').create());

export const getReviewListByEmployeeIdFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/review-results/').method('get').create()
);

export const sendCalibrationByEmployeeIdFx = createRequestFx(
  fetcher.path('/salary-requests/:createCalibration').method('post').create()
);

export const getRequestSalaryProcessesFx = createRequestFx(fetcher.path('/salary-requests/').method('get').create());

export const sendChristmasGameResultsFx = createRequestFx(fetcher.path('/quiz/answers/').method('post').create());

export const getQuizQuestions = createRequestFx(fetcher.path('/quiz/questions/').method('get').create());

export const getPastReviewsForReviewerByEmployeeId = createRequestFx(
  fetcher.path('/employees/{employee_id}/reviews/:get-for-reviewer').method('get').create()
);

export const getQuizWinners = createRequestFx(fetcher.path('/quiz/best-results/').method('get').create());

export const getEmployeeScore = createRequestFx(fetcher.path('/quiz/user-result/').method('get').create());

export const getEmployeeAnswers = createRequestFx(fetcher.path('/quiz/user-answers/').method('get').create());

export const closeReviewProcessFx = createRequestFx(
  fetcher.path('/talents/review-processes/{process_id}/').method('put').create()
);

export const getReviewProcessHistoryFx = createRequestFx(
  fetcher.path('/system-actions/{review_process_id}/review-process/').method('get').create()
);

export const getFeedbackProcessHistoryFx = createRequestFx(
  fetcher.path('/system-actions/{soft_skill_process_id}/soft-skill-process/').method('get').create()
);

export const getSalaryRequestProcessHistoryFx = createRequestFx(
  fetcher.path('/system-actions/{salary_request_id}/salary-request/').method('get').create()
);

export const getEmployeeGratitudesFx = createRequestFx(fetcher.path('/gratitudes/employees/').method('get').create());

export const sendGratitudeToEmployeeFx = createRequestFx(fetcher.path('/gratitudes/employees/').method('post').create());

export const getTeamGratitudesFx = createRequestFx(fetcher.path('/gratitudes/teams/').method('get').create());

export const sendGratitudeToTeamFx = createRequestFx(fetcher.path('/gratitudes/teams/').method('post').create());

export const getKarmaHistoryByEmployeeIdFx = createRequestFx(fetcher.path('/karma/events/').method('get').create());

export const createKarmaEventFx = createRequestFx(fetcher.path('/karma/events/').method('post').create());

export const getKarmaEventTypesFx = createRequestFx(fetcher.path('/karma/event-types/').method('get').create());

export const getMainstayListFx = createRequestFx(fetcher.path('/employees/sdit-mainstay/').method('get').create());

export const getManagedTeamsByEmployeeIdFx = createRequestFx(
  fetcher.path('/teams/{employee_id}/managed-teams').method('get').create()
);

export const getTeamPulseByTeamIdFx = createRequestFx(fetcher.path('/teams/{team_id}/pulse-team').method('get').create());

export const getProductsListFx = createRequestFx(fetcher.path('/products/').method('get').create());

export const getTechnologiesFx = createRequestFx(fetcher.path('/products/technologies/').method('get').create());

export const updateProductBySlugFx = createRequestFx(fetcher.path('/products/{slug}/').method('put').create());

export const transferToTeamByTeamIdFx = createRequestFx(fetcher.path('/teams/{team_id}:wantHere').method('post').create());

export const updateTeamFx = createRequestFx(fetcher.path('/teams/{team_id}:updateMembership').method('put').create());

export const getTeamInfoFx = createRequestFx(fetcher.path('/teams/{team_id}/team-info').method('get').create());

export const getAboutTeamInfoFx = createRequestFx(fetcher.path('/teams/{team_id}/team-info').method('get').create());

export const updateTeamInfoFx = createRequestFx(fetcher.path('/teams/{team_id}/team-info').method('patch').create());

export const addTeamImageFx = createRequestFx(fetcher.path('/teams/{team_id}/image').method('post').create());

export const deleteTeamImageFx = createRequestFx(
  fetcher.path('/teams/{team_id}/image/{team_image_id}').method('delete').create()
);

export const getSurveyQuestionsFx = createRequestFx(fetcher.path('/surveys/').method('get').create());

export const postSurveyAnswersFx = createRequestFx(fetcher.path('/surveys/{survey_id}/').method('post').create());

export const getTodoListFx = createRequestFx(fetcher.path('/todos/').method('get').create());

export const postTodoFx = createRequestFx(fetcher.path('/todos/{task_id}/:complete').method('post').create());

export const getTeamMetricsFx = createRequestFx(fetcher.path('/teams/{team_id}/team-metrics').method('get').create());

export const updateEmployeeProfileFx = createRequestFx(
  fetcher.path('/employees/{employee_id}/profile/').method('patch').create()
);

export const getAttributesFx = createRequestFx(fetcher.path('/references/attributes/').method('get').create());

export const getHobbiesFx = createRequestFx(fetcher.path('/employees/hobbies/').method('get').create());

export const getAdaptationsListFx = createRequestFx(fetcher.path('/adaptation-processes/').method('get').create());

export const getMentorsFx = createRequestFx(fetcher.path('/employees/mentors/').method('get').create());

export const getAdaptationByIdFx = createRequestFx(fetcher.path('/adaptation-processes/{process_id}/').method('get').create());

export const getAdaptationByEmployeeIdFx = createRequestFx(
  fetcher.path('/adaptation-processes/{employee_id}/:full').method('get').create()
);

export const updateAdaptationByIdFx = createRequestFx(
  fetcher.path('/adaptation-processes/{process_id}/').method('patch').create()
);

export const getOccasionsFx = createRequestFx(fetcher.path('/occasions/').method('get').create());

export const getOccasionTypesFx = createRequestFx(fetcher.path('/occasions/types/').method('get').create());

export const getOccasionUsefulLinksTypesFx = createRequestFx(
  fetcher.path('/occasions/useful-link-types/').method('get').create()
);

export const toggleLikeOccasionFx = createRequestFx(fetcher.path('/occasions/{occasion_id}:toggleLike').method('patch').create());

export const createOccasionFx = createRequestFx(fetcher.path('/occasions/').method('post').create());

export const getImagesFx = createRequestFx(fetcher.path('/files/images/').method('get').create());

export const getOccasionFx = createRequestFx(fetcher.path('/occasions/{occasion_id}/').method('get').create());

export const editOccasionFx = createRequestFx(fetcher.path('/occasions/{occasion_id}/').method('patch').create());

export const getFeaturesFx = createRequestFx(fetcher.path('/features/').method('get').create());

export const getHoroscopeFx = createRequestFx(fetcher.path('/horoscopes/:getByEmployee').method('get').create());
